import type { AiApplicationsContract } from '@terra/shared-contracts-ai-applications';
import type {
  ApplicationsContract,
  UpsertApplicationInternalAccessBodySchema,
} from '@terra/shared-contracts-applications';
import type { ConnectorManagerContract } from '@terra/shared-contracts-connector-manager';
import type { gitContract } from '@terra/shared-contracts-git';
import {
  INTEGRATION_TYPES,
  PREVENTION_CONTROL_TYPES,
  PREVENTION_TARGET_MATCH_KINDS,
  type CreatePreventionTargetMappingBody,
  type IntegrationsContract,
  type PreventionWafVendor,
} from '@terra/shared-contracts-integrations';
import { aiApplicationsClient } from '@terra/shared-rest-clients-ai-applications';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { connectorManagerClient } from '@terra/shared-rest-clients-connector-manager';
import { gitClient } from '@terra/shared-rest-clients-git';
import { integrationsClient } from '@terra/shared-rest-clients-integrations';
import type { z } from 'zod';
import {
  tenantAwareApiWrapper,
  withOrchestratorClient,
  withServiceClient,
} from '../api-core';

type UpsertApplicationInternalAccessBody = z.infer<
  typeof UpsertApplicationInternalAccessBodySchema
>;

export const portalApplicationTargetSettingsApi = {
  listApplicationGitIntegrations: (applicationId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getApplicationGitIntegrations>({
      endpointFn: (headers) =>
        gitClient.getApplicationGitIntegrations({
          params: { applicationId },
          ...withServiceClient(headers, 'git'),
        }),
    }),

  getTenantGitIntegrationDetails: (integrationId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getTenantGitIntegrationById>({
      endpointFn: (headers) =>
        gitClient.getTenantGitIntegrationById({
          params: { integrationId },
          ...withServiceClient(headers, 'git'),
        }),
    }),

  validateApplicationGitIntegrations: (applicationId: string) =>
    tenantAwareApiWrapper<typeof gitContract.validateGitIntegration>({
      endpointFn: (headers) =>
        gitClient.validateGitIntegration({
          params: { applicationId },
          ...withServiceClient(headers, 'git'),
        }),
    }),

  disconnectApplicationGitIntegration: ({
    applicationId,
    integrationId,
  }: {
    applicationId: string;
    integrationId: string;
  }) =>
    tenantAwareApiWrapper<typeof gitContract.deleteApplicationGitIntegration>({
      endpointFn: (headers) =>
        gitClient.deleteApplicationGitIntegration({
          params: { applicationId, integrationId },
          ...withServiceClient(headers, 'git'),
        }),
      useBodyMessage: true,
    }),

  getApplicationInternalAccess: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.getApplicationInternalAccess
    >({
      endpointFn: (headers) =>
        applicationsClient.getApplicationInternalAccess({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  upsertApplicationInternalAccess: ({
    applicationId,
    body,
  }: {
    applicationId: string;
    body: UpsertApplicationInternalAccessBody;
  }) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.upsertApplicationInternalAccess
    >({
      endpointFn: (headers) =>
        applicationsClient.upsertApplicationInternalAccess({
          params: { id: applicationId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  testApplicationInternalAccess: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.testApplicationInternalAccess
    >({
      endpointFn: (headers) =>
        applicationsClient.testApplicationInternalAccess({
          params: { id: applicationId },
          body: {},
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  listTargetTwingateNetworks: () =>
    tenantAwareApiWrapper<typeof ConnectorManagerContract.listTwingateNetworks>(
      {
        endpointFn: (headers) =>
          connectorManagerClient.listTwingateNetworks({
            ...withOrchestratorClient(headers),
          }),
      },
    ),

  listTargetConnectors: () =>
    tenantAwareApiWrapper<typeof ConnectorManagerContract.listConnectors>({
      endpointFn: (headers) =>
        connectorManagerClient.listConnectors({
          query: {},
          ...withOrchestratorClient(headers),
        }),
    }),

  listWafPreventionConnections: () =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.listPreventionConnections
    >({
      endpointFn: (headers) =>
        integrationsClient.listPreventionConnections({
          query: {
            controlType: PREVENTION_CONTROL_TYPES.WAF,
            provider: INTEGRATION_TYPES.GENERIC_WAF,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  getAwsCloudConnectionStatus: () =>
    tenantAwareApiWrapper<typeof IntegrationsContract.getCloudConnectionStatus>(
      {
        endpointFn: (headers) =>
          integrationsClient.getCloudConnectionStatus({
            params: { provider: INTEGRATION_TYPES.AWS },
            ...withOrchestratorClient(headers),
          }),
      },
    ),

  listApplicationPreventionMappings: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.listPreventionTargetMappings
    >({
      endpointFn: (headers) =>
        integrationsClient.listPreventionTargetMappings({
          query: {
            matchKind: PREVENTION_TARGET_MATCH_KINDS.APPLICATION,
            matchValue: applicationId,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  createPreventionTargetMapping: (body: CreatePreventionTargetMappingBody) =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.createPreventionTargetMapping
    >({
      endpointFn: (headers) =>
        integrationsClient.createPreventionTargetMapping({
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  deletePreventionTargetMapping: (mappingId: string) =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.deletePreventionTargetMapping
    >({
      endpointFn: (headers) =>
        integrationsClient.deletePreventionTargetMapping({
          params: { mappingId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  ensureAwsWafPreventionConnection: () =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.ensureAwsWafPreventionConnection
    >({
      endpointFn: (headers) =>
        integrationsClient.ensureAwsWafPreventionConnection({
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  discoverPreventionWafTargets: ({
    vendor,
    connectionId,
  }: {
    vendor: PreventionWafVendor;
    connectionId?: string;
  }) =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.discoverPreventionWafTargets
    >({
      endpointFn: (headers) =>
        integrationsClient.discoverPreventionWafTargets({
          query: { vendor, ...(connectionId ? { connectionId } : {}) },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  getApplicationWafSuggestions: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof IntegrationsContract.getApplicationWafSuggestions
    >({
      endpointFn: (headers) =>
        integrationsClient.getApplicationWafSuggestions({
          params: { applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteApplicationTarget: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.deleteApplication>({
      endpointFn: (headers) =>
        applicationsClient.deleteApplication({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  deleteAiApplicationTarget: (aiApplicationId: string) =>
    tenantAwareApiWrapper<typeof AiApplicationsContract.delete>({
      endpointFn: (headers) =>
        aiApplicationsClient.delete({
          params: { aiApplicationId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),
};
